import {
  APAC_COUNTRIES,
  AU_COUNTRY,
  EU_COUNTRIES,
  JP_COUNTRY,
  NZ_COUNTRY,
  UK_COUNTRIES,
  US_COUNTRY
} from '@portal/constants/locations';
import { LocaleEnum } from '@portal/schema/locale';

export interface Hreflang {
  lang: string; // hreflang value, e.g., "en-US"
  locale: LocaleEnum; // source locale enum
  slug: string; // path/slug
}

// --- helpers ---------------------------------------------------------------

const normalizeTag = (lang: string, country: string) =>
  `${lang.toLowerCase()}-${country.toUpperCase()}`;

const toTags = (lang: string, countries: readonly string[]) =>
  countries.map((c) => normalizeTag(lang, c));

// Precompute static sets once.
const EU_COUNTRIES_NO_XK: readonly string[] = EU_COUNTRIES.filter((c) => c !== 'XK');

// Useful for filtering previews/dev environments.
const NON_PROD_LOCALES = new Set<LocaleEnum>([
  LocaleEnum.enDev,
  LocaleEnum.enQa,
  LocaleEnum.enAud,
  LocaleEnum.enNzd,
  LocaleEnum.deQa,
  LocaleEnum.enEud,
  LocaleEnum.deEud,
  LocaleEnum.enUkd
]);

type LocaleTagSpec = readonly string[];

// Build the mapping as “arrays of tags” for clarity.
// Using `satisfies` forces exhaustiveness against LocaleEnum.
const LOCALE_TO_TAGS = {
  [LocaleEnum.enUS]: toTags('en', [US_COUNTRY]),
  [LocaleEnum.apacUS]: toTags('en', APAC_COUNTRIES),
  [LocaleEnum.enAU]: toTags('en', [AU_COUNTRY]),
  [LocaleEnum.enNZ]: toTags('en', [NZ_COUNTRY]),
  [LocaleEnum.enUK]: toTags('en', UK_COUNTRIES),
  [LocaleEnum.enEU]: toTags('en', EU_COUNTRIES_NO_XK),

  [LocaleEnum.deEU]: toTags('de', ['DE']),
  [LocaleEnum.enJP]: toTags('en', [JP_COUNTRY]),
  [LocaleEnum.jaJP]: toTags('ja', [JP_COUNTRY]),
  [LocaleEnum.seEU]: toTags('sv', ['SE']),
  [LocaleEnum.nlEU]: toTags('nl', ['NL']),
  [LocaleEnum.esEU]: toTags('es', ['ES']),
  [LocaleEnum.frEU]: toTags('fr', ['FR']),

  // non-prod
  [LocaleEnum.enDev]: [],
  [LocaleEnum.enQa]: [],
  [LocaleEnum.enAud]: [],
  [LocaleEnum.enNzd]: [],
  [LocaleEnum.deQa]: [],
  [LocaleEnum.enEud]: [],
  [LocaleEnum.deEud]: [],
  [LocaleEnum.enUkd]: []
} satisfies Record<LocaleEnum, LocaleTagSpec>;

type GetHreflangOptions = {
  /** Exclude non-production locales (defaults to true) */
  excludeNonProd?: boolean;
  /** Include an x-default entry (e.g., to canonicalize a preferred locale’s URL) */
  xDefault?: {
    slug: string; // absolute or relative URL/slug you consider the default
  };
  /** Sort results alphabetically for stable output (defaults to true) */
  sort?: boolean;
  /** Also emit region-independent language fallbacks like `en`, `de`, `ja` (defaults to true) */
  includeLanguageOnly?: boolean;
};

export const getHreflangLinks = (
  slug: string,
  opts: GetHreflangOptions = {}
): ReadonlyArray<Hreflang> => {
  const { excludeNonProd = true, xDefault, sort = true, includeLanguageOnly = true } = opts;

  const entries = Object.entries(LOCALE_TO_TAGS).flatMap(([localeKey, tags]) => {
    const locale = localeKey as LocaleEnum;
    if (excludeNonProd && NON_PROD_LOCALES.has(locale)) return [];
    return tags.map((tag) => ({ lang: tag, locale, slug }));
  });

  // dedupe in case of overlaps between groups
  const byLang = new Map<string, Hreflang>();
  entries.forEach((entry) => {
    if (!byLang.has(entry.lang)) byLang.set(entry.lang, entry);
  });

  const list = Array.from(byLang.values());
  if (sort) list.sort((a, b) => a.lang.localeCompare(b.lang));

  // Optionally add language-only fallbacks (e.g., `en`, `de`) if not present.
  if (includeLanguageOnly) {
    // Determine default representative locale per language for stable output.
    const languageToDefaultLocale: Record<string, LocaleEnum> = {
      en: LocaleEnum.enUS,
      de: LocaleEnum.deEU,
      ja: LocaleEnum.jaJP,
      sv: LocaleEnum.seEU,
      nl: LocaleEnum.nlEU,
      es: LocaleEnum.esEU,
      fr: LocaleEnum.frEU
    };

    const presentLanguageOnly = new Set(
      list.filter((e) => !e.lang.includes('-')).map((e) => e.lang)
    );
    const languagesWithRegional = new Set<string>();
    list.forEach((e) => {
      if (e.lang.includes('-')) languagesWithRegional.add(e.lang.split('-')[0]);
    });

    const additions: Hreflang[] = [];
    languagesWithRegional.forEach((language) => {
      if (presentLanguageOnly.has(language)) return;

      const representativeLocale = languageToDefaultLocale[language];
      // Fall back to the first regional entry's locale if no explicit default is defined.
      const fallbackLocale =
        representativeLocale ||
        list.find((e) => e.lang.startsWith(`${language}-`))?.locale ||
        LocaleEnum.enUS;

      additions.push({ lang: language, locale: fallbackLocale, slug });
    });

    if (additions.length) {
      additions.forEach((a) => byLang.set(a.lang, a));
      // Rebuild list to include additions, then sort for stability
      const withAdditions = Array.from(byLang.values());
      if (sort) withAdditions.sort((a, b) => a.lang.localeCompare(b.lang));
      // Replace list contents without reassigning to keep local semantics simple
      list.length = 0;
      list.push(...withAdditions);
    }
  }

  // Optionally add x-default (note: x-default has no country). Force en-US as the default locale.
  if (xDefault?.slug) {
    list.unshift({ lang: 'x-default', locale: LocaleEnum.enUS, slug: xDefault.slug });
  }

  return list;
};
